import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import ModalBase from 'components/modalBase/index';
import Icons from 'images/icons';
import { useDeviceSize } from 'hooks/useDeviceSize';
import { suppressCloseConfirm } from 'hooks/closeConfirm';
import { EC_TIMEOUT_MS } from 'utils/ecTransitionUtil';

// EC動線（EC連携）で予約が完了されないまま放置された場合に、
// EC動線開始（ecFlow.startedAt）から1時間経過で表示するタイムアウトモーダル。
// 無操作リセットは行わず、動線開始からの絶対経過時間で判定する。
const EcTimeoutModal = () => {
  const { fromEc, startedAt } = useSelector(state => state.ecFlow);
  const { isSp } = useDeviceSize();

  // 一度表示したら閉じない仕様のため、モーダルの開閉はローカルstateで管理する
  // （ecFlowはclearしない＝背後のロゴ・フッターリンク無効化等の離脱制御を維持する）
  const [isEcTimeoutModalOpen, setIsEcTimeoutModalOpen] = useState(false);

  // window.close()が拒否された場合のフォールバック遷移（about:blank）用タイマー
  const closeFallbackTimerRef = useRef(null);

  useEffect(() => {
    if (!fromEc || !startedAt) {
      return;
    }

    let timerId;

    // 残り時間を計算し、経過済みなら即表示、未経過ならその分だけ setTimeout を張り直す
    const scheduleTimeout = () => {
      const remainingMs = startedAt + EC_TIMEOUT_MS - Date.now();
      if (remainingMs <= 0) {
        setIsEcTimeoutModalOpen(true);
        return;
      }
      timerId = setTimeout(() => setIsEcTimeoutModalOpen(true), remainingMs);
    };

    // バックグラウンドタブでは setTimeout がスロットリングされ発火が遅延することがあるため、
    // フォアグラウンド復帰時に残り時間を再計算してタイマーを張り直す
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      clearTimeout(timerId);
      scheduleTimeout();
    };

    scheduleTimeout();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fromEc, startedAt]);

  // アンマウント時にフォールバック遷移用タイマーが残らないようクリーンアップする
  useEffect(() => {
    return () => clearTimeout(closeFallbackTimerRef.current);
  }, []);

  const handleClose = () => {
    // タブを閉じる際、beforeunloadの離脱確認ダイアログを出さないよう事前に抑止する
    suppressCloseConfirm();
    // EC側から同一タブでのリダイレクトで流入した場合など、スクリプトで開かれていない
    // タブではブラウザの制約によりwindow.close()が拒否されることがある。
    // 閉じられた場合はこの後の処理は実行されないため、拒否された場合のフォールバックとして
    // 少し待ってから about:blank へ遷移させ、モーダルが残り続けないようにする
    window.close();
    closeFallbackTimerRef.current = setTimeout(() => {
      // suppressCloseConfirmの抑止フラグは1回の離脱で消費される仕様のため、
      // window.close()が拒否されて未消費のケース・消費済みのケースのどちらでも
      // 離脱確認ダイアログが出ないよう、遷移直前に再度呼び出しておく
      suppressCloseConfirm();
      window.location.href = 'about:blank';
    }, 300);
  };

  return (
    <ModalBase
      isModalOpen={isEcTimeoutModalOpen}
      // 「閉じる」ボタン以外では閉じられないよう、背景クリックでは何もしない
      toggleModal={() => {}}
      className="flex flex-col items-center overflow-auto px-4 pb-[60px] pt-14 sm:px-10 lg:px-20 lg:pb-[104px] lg:pt-24"
    >
      <label className="text-base mx-4 whitespace-pre-wrap text-center font-bold text-navy sm:mx-8 lg:mx-12">
        一定時間操作がなかったため、{'\n'}
        予約手続きを終了しました。{'\n'}
        購入・予約を続ける場合は、{'\n'}
        公式通販サイトから{'\n'}
        再度お手続きをお願いいたします。
      </label>
      <div className="mt-6 lg:mt-8">
        {/* 予約手続きは終了済みのため同一タブ遷移が確定仕様。target="_blank"を強制する
            components/linked の Linked は使わず、isLine かつ linkIcon="R" の見た目のみを
            踏襲した専用の a タグを直接実装する */}
        <a
          href={process.env.REACT_APP_EC_BASE_URL}
          onClick={() => suppressCloseConfirm()}
          className="group relative flex w-fit cursor-pointer items-center text-[14px] text-link after:absolute after:bottom-0.5 after:w-full after:border-b after:border-solid after:border-[#23629B] after:border-link hover:text-link-hover after:hover:border-link-hover sm:text-[18px]"
        >
          公式通販サイトへ
          <Icons.Image
            src={Icons.linkR}
            className={classNames('group-hover:opacity-70', {
              'w-[14px]': isSp,
            })}
          />
        </a>
      </div>
      <button
        type="button"
        onClick={handleClose}
        className="mt-10 text-[14px] font-bold text-navy sm:text-[18px] lg:mt-20"
      >
        閉じる
      </button>
    </ModalBase>
  );
};

export default EcTimeoutModal;
